import { Injectable } from '@angular/core';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { CradlepointLocation } from '@app/shared/models/cradlepoint/cradlepoint-location.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ChainWideCradlepointsService {
    constructor(
        private cradlepointService: CradlepointsService,
        private locationsService: LocationsService
    ) { }

    async getAllCradlepoints(): Promise<CradlepointLocation[]> {
        const cradlepointLocations: CradlepointLocation[] = [];
        try {
            const observable = await this.cradlepointService.getAllCradlepoints();
            const cradlepoints_from_response = await firstValueFrom(observable);
            if (typeof cradlepoints_from_response === 'string') {
                console.log("Cradlepoints timed out!");
                return cradlepointLocations;
            }
            Object.entries(cradlepoints_from_response["message"]).forEach(location_data => {
                let cradlepointLocation: CradlepointLocation = new CradlepointLocation;
                cradlepointLocation.cradlepoints = [];
                cradlepointLocation.storeNumber = location_data[0];
                for (let cradlepoint of location_data[1] as Cradlepoint[]) {
                    cradlepointLocation.cradlepoints.push(cradlepoint);
                }
                cradlepointLocations.push(cradlepointLocation);
            });
        } catch (error) {
            console.log(error);
        }
        return cradlepointLocations;
    }

    async getAllLocations(): Promise<Location[]> {
        const locations: Location[] = [];
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    async createDataSource(locations?: Location[]): Promise<CradlepointLocation[]> {
        let locationsToUse: Location[];

        if (locations == null) {
            locationsToUse = await this.getAllLocations();
        } else {
            locationsToUse = locations;
        }

        const cradlepointLocations = await this.getAllCradlepoints();

        let locationMap = new Map<string, Location>();
        locationsToUse.forEach(location => {
            locationMap.set(location.number, location);
        });

        for (let cradlepointLocation of cradlepointLocations) {
            if (locationMap.get(cradlepointLocation.storeNumber)) {
                cradlepointLocation.location = locationMap.get(cradlepointLocation.storeNumber);
            } else {
                const startingIndex = cradlepointLocation.cradlepoints[0].name.search('[0-9]{5}');
                const storeNumber = cradlepointLocation.cradlepoints[0].name.substring(startingIndex, startingIndex + 5);
                cradlepointLocation.location = {
                    'name': "",
                    'status': "",
                    'timezone': "",
                    'number': storeNumber
                } as Location;
            }
        }

        return cradlepointLocations;
    }
}
